import { Link } from "react-router-dom";
import { BookOpen, Lightbulb, FileText, ShieldCheck, ArrowRight, ClipboardCheck, FileSignature, Calculator } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
} from "../components/ui";
import { Seo } from "../lib/head";

const TILES = [
  { icon: BookOpen, title: "Blog", body: "Product updates, regulatory commentary, clinical deep-dives.", to: "/blog" },
  { icon: FileText, title: "Docs", body: "Implementation guides and API references.", to: "/docs" },
  { icon: ShieldCheck, title: "Trust center", body: "Security, HIPAA, BAA, DPA, subprocessors, and SLA.", to: "/trust" },
  { icon: Lightbulb, title: "Press kit", body: "Logo lockups, color palette, brand guidelines.", to: "/press" },
];

const TOOLS = [
  { icon: ClipboardCheck, title: "F-tag readiness checklist", body: "Map your monthly review to F756/F757/F758/F605/F881 before your next survey.", to: "/resources/f-tag-readiness-checklist" },
  { icon: FileSignature, title: "GDR documentation template", body: "A defensible gradual-dose-reduction record — attempt, rationale, taper, sign-off.", to: "/resources/gdr-documentation-template" },
  { icon: Calculator, title: "MRR ROI calculator", body: "Estimate the hours and dollars your practice frees each month.", to: "/roi-calculator" },
];

export function Resources() {
  return (
    <>
      <Seo title="Resources — WeConsult Rx" description="Free tools and guides for consultant pharmacists: F-tag readiness checklist, GDR documentation template, MRR ROI calculator, plus blog, docs, and trust center." path="/resources" />
    <Section>
      <Container>
        <div className="max-w-3xl">
          <Eyebrow>Resources</Eyebrow>
          <Heading level={1} className="mt-3">
            Free tools and guides for consultant pharmacists.
          </Heading>
          <Lede className="mt-6">
            Survey-prep checklists, documentation templates, an ROI calculator — plus our blog, docs, and trust
            center. We share our work openly.
          </Lede>
        </div>

        <Heading level={2} className="mt-12">
          Free tools &amp; guides
        </Heading>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Card key={t.title} as="article" className="h-full">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <Heading level={3} className="mt-4">
                  {t.title}
                </Heading>
                <p className="mt-2 text-[0.9375rem] text-ink-600">{t.body}</p>
                <Link
                  to={t.to}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
                >
                  Open
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Card>
            );
          })}
        </div>

        <Heading level={2} className="mt-14">
          More from WeConsult Rx
        </Heading>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Card key={t.title}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <Heading level={3}>{t.title}</Heading>
                </div>
                <p className="mt-4 text-ink-600">{t.body}</p>
                <Link
                  to={t.to}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
                >
                  Explore {t.title}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Card>
            );
          })}
        </div>
      </Container>
    </Section>
    </>
  );
}
